import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Card } from "@tremor/react";
import { cn } from "@/lib/utils";
import {
  useFindings, useFindingsSummary, useFinding,
  type Finding, type FindingStatus, type Severity,
} from "@/lib/queries";
import { FindingDetailDrawer } from "@/components/FindingDetailDrawer";
import { FindingSourceBadge } from "@/components/FindingSourceBadge";

const SEVERITY_STYLES: Record<string, string> = {
  critical: "bg-red-500/10 text-red-600 border-red-500/20 dark:text-red-400",
  high:     "bg-orange-500/10 text-orange-600 border-orange-500/20 dark:text-orange-400",
  medium:   "bg-amber-500/10 text-amber-600 border-amber-500/20 dark:text-amber-400",
  low:      "bg-emerald-500/10 text-emerald-600 border-emerald-500/20 dark:text-emerald-400",
  info:     "bg-blue-500/10 text-blue-600 border-blue-500/20 dark:text-blue-400",
};

const STATUS_TILE: Record<FindingStatus, string> = {
  open:      "bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/30",
  confirmed: "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/30",
  dismissed: "bg-muted text-muted-foreground border-border",
  resolved:  "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30",
};

const STATUSES: (FindingStatus | "all")[] = ["all", "open", "confirmed", "resolved", "dismissed"];
const SEVERITIES: (Severity | "all")[] = ["all", "critical", "high", "medium", "low", "info"];

function fmtTime(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  const sameDay =
    d.getFullYear() === today.getFullYear() &&
    d.getMonth() === today.getMonth() &&
    d.getDate() === today.getDate();
  return sameDay
    ? d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : d.toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

function FilterPill({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "rounded-full border px-3 py-1 text-xs font-medium transition-colors",
        active
          ? "border-primary bg-primary text-primary-foreground"
          : "border-border bg-background text-muted-foreground hover:bg-accent hover:text-accent-foreground",
      )}
    >
      {label}
    </button>
  );
}

export default function Findings() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [status, setStatus] = useState<FindingStatus | "all">("open");
  const [severity, setSeverity] = useState<Severity | "all">("all");
  const [selectedId, setSelectedId] = useState<number | null>(() => {
    const fromUrl = searchParams.get("id");
    return fromUrl ? Number(fromUrl) : null;
  });

  const summary = useFindingsSummary();
  const list = useFindings({
    status: status === "all" ? undefined : status,
    severity: severity === "all" ? undefined : severity,
    limit: 200,
  });
  // Always fetch the selected finding by id so deep-links work even if the
  // current filter wouldn't include it.
  const selected = useFinding(selectedId);

  // Keep the URL in sync — `?id=42` makes findings deep-linkable from
  // Telegram messages, email, anywhere.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (selectedId !== null) next.set("id", String(selectedId));
    else next.delete("id");
    setSearchParams(next, { replace: true });
  }, [selectedId, searchParams, setSearchParams]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Findings</h1>
        <p className="text-sm text-muted-foreground">
          What Tyrael decided you should care about. Each row is a structured
          finding extracted from an audit. Click to investigate and provide
          a verdict.
        </p>
      </div>

      {/* Status tiles */}
      {summary.data && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {(["open", "confirmed", "resolved", "dismissed"] as FindingStatus[]).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setStatus(status === s ? "all" : s)}
              className={cn(
                "rounded-lg border px-4 py-3 text-left transition-shadow hover:shadow-sm",
                STATUS_TILE[s],
                status === s && "ring-2 ring-current",
              )}
            >
              <div className="text-[10px] font-semibold uppercase tracking-wider opacity-80">{s}</div>
              <div className="mt-1 text-2xl font-semibold tabular-nums">
                {summary.data.by_status[s]}
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Status pills + severity pills */}
      <div className="flex flex-wrap items-center gap-2 border-b border-border pb-3">
        <span className="text-xs uppercase tracking-wider text-muted-foreground">Status</span>
        {STATUSES.map((s) => (
          <FilterPill key={s} label={s} active={status === s} onClick={() => setStatus(s)} />
        ))}
        <span className="ml-4 text-xs uppercase tracking-wider text-muted-foreground">Severity</span>
        {SEVERITIES.map((s) => (
          <FilterPill key={s} label={s} active={severity === s} onClick={() => setSeverity(s)} />
        ))}
      </div>

      {/* Findings table */}
      <Card className="p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="border-b border-border bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-4 py-3 w-24">Severity</th>
              <th className="px-4 py-3 w-20">Status</th>
              <th className="px-4 py-3 w-24">Source</th>
              <th className="px-4 py-3">Title</th>
              <th className="px-4 py-3">Hosts</th>
              <th className="px-4 py-3">When</th>
            </tr>
          </thead>
          <tbody>
            {list.isLoading && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-muted-foreground">Loading…</td></tr>
            )}
            {list.isError && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-red-500">Failed to load findings.</td></tr>
            )}
            {list.data?.items.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-muted-foreground">No findings match the current filters.</td></tr>
            )}
            {list.data?.items.map((f: Finding) => (
              <tr
                key={f.id}
                onClick={() => setSelectedId(f.id)}
                className="cursor-pointer border-b border-border last:border-0 transition-colors hover:bg-muted/40"
              >
                <td className="px-4 py-3">
                  <span
                    className={cn(
                      "inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold uppercase tracking-wide",
                      SEVERITY_STYLES[f.severity],
                    )}
                  >{f.severity}</span>
                </td>
                <td className="px-4 py-3">
                  <span className="rounded-full border border-border bg-muted/40 px-2 py-0.5 text-[10px] font-medium uppercase">
                    {f.status}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <FindingSourceBadge source={f.source} size="xs" />
                </td>
                <td className="px-4 py-3">
                  <div className="font-medium">{f.title}</div>
                  {f.category && (
                    <div className="mt-0.5 text-[11px] font-mono text-muted-foreground">{f.category}</div>
                  )}
                </td>
                <td className="px-4 py-3 text-xs text-muted-foreground">
                  {f.affected_hosts.length === 0 && f.affected_actors.length === 0 ? (
                    <span>—</span>
                  ) : f.affected_hosts.length > 0 ? (
                    f.affected_hosts.length <= 2
                      ? f.affected_hosts.join(", ")
                      : `${f.affected_hosts[0]} +${f.affected_hosts.length - 1}`
                  ) : (
                    <span className="font-mono text-[11px]">{f.affected_actors[0]}{f.affected_actors.length > 1 ? ` +${f.affected_actors.length - 1}` : ""}</span>
                  )}
                </td>
                <td className="px-4 py-3 text-xs text-muted-foreground tabular-nums">
                  {f.source === "audit" && f.audit_id ? `audit #${f.audit_id} · ${fmtTime(f.audit_run_at!)}` : fmtTime(f.created_at)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <FindingDetailDrawer
        finding={selected.data ?? null}
        open={selectedId !== null}
        onOpenChange={(o) => !o && setSelectedId(null)}
      />
    </div>
  );
}
